import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import {
  SearchShipmentByInvoiceDto,
  ShipmentByInvoiceDoc,
  ShipmentByInvoiceItem,
  ShipmentByInvoiceListRow
} from 'src/typings';
import { environment } from 'src/environments/environment';
import { normalizeDateRangePayload } from 'src/app/shared/utils/date-filter.util';
import { PRESERVE_HTTP_ERROR } from 'src/app/shared/utils/preserve-http-error.context';
import { SaleStockPlan } from '../export/sale-stock-plan';

export interface ShipmentByInvoiceProductPayload {
  product_id: number;
  quan: number;
}

export interface ShipmentByInvoiceDocument extends ShipmentByInvoiceDoc {
  stockAddressingVersion?: 0 | 1 | 2;
}

export interface CreateShipmentByInvoiceDto {
  date?: Date;
  invoice_id: number;
  warehouse_id: number;
  products: ShipmentByInvoiceProductPayload[];
  allowDebtLimitExceeded?: boolean;
  stock_addressing_version?: 1 | 2;
}

export interface SaveShipmentByInvoiceDto {
  id: number;
  date?: Date;
  invoice_id?: number;
  warehouse_id?: number;
  products?: ShipmentByInvoiceProductPayload[];
  allowDebtLimitExceeded?: boolean;
}

export type ShipmentByInvoicePrintType = 'article' | 'id';

@Injectable({
  providedIn: 'root'
})
export class ShipmentByInvoiceService {
  constructor(private readonly http: HttpClient) {}

  stockPlan(payload: {
    invoice_id: number;
    warehouse_id: number;
    products: ShipmentByInvoiceProductPayload[];
  }) {
    return this.http.post<SaleStockPlan>(
      `${environment.BACKEND}/doc/shipment-by-invoice/stock-plan`, payload,
      { context: new HttpContext().set(PRESERVE_HTTP_ERROR, true) }
    );
  }

  create(payload: CreateShipmentByInvoiceDto) {
    return this.http
      .post<ShipmentByInvoiceDocument>(`${environment.BACKEND}/doc/shipment-by-invoice/create`, payload)
      .pipe(map((doc) => this.normalizeDoc(doc)));
  }

  save(payload: SaveShipmentByInvoiceDto) {
    return this.http
      .put<ShipmentByInvoiceDocument>(`${environment.BACKEND}/doc/shipment-by-invoice/save`, payload)
      .pipe(map((doc) => this.normalizeDoc(doc)));
  }

  search(payload: SearchShipmentByInvoiceDto) {
    const normalizedPayload = normalizeDateRangePayload(payload);
    return this.http
      .post<ShipmentByInvoiceListRow[]>(
        `${environment.BACKEND}/doc/shipment-by-invoice/search`,
        normalizedPayload
      )
      .pipe(map((rows) => rows.map((row) => this.normalizeListRow(row))));
  }

  getList() {
    return this.http
      .get<ShipmentByInvoiceListRow[]>(`${environment.BACKEND}/doc/shipment-by-invoice/list`)
      .pipe(map((rows) => rows.map((row) => this.normalizeListRow(row))));
  }

  getOne(id: number) {
    return this.http
      .get<ShipmentByInvoiceDocument>(`${environment.BACKEND}/doc/shipment-by-invoice/${id}`)
      .pipe(map((doc) => this.normalizeDoc(doc)));
  }

  getProductsInDoc(id: number) {
    return this.http.get<
      {
        id: number;
        description: string;
        brand: string;
        article: string;
        quan: number | string;
        price: number | string;
        sum: number | string;
      }[]
    >(`${environment.BACKEND}/doc/shipment-by-invoice/${id}/products`);
  }

  delete(id: number) {
    return this.http.delete<void>(`${environment.BACKEND}/doc/shipment-by-invoice/${id}`);
  }

  getPdfUrl(id: number, type: ShipmentByInvoicePrintType = 'article') {
    if (type === 'id') {
      return `${environment.BACKEND}/doc/shipment-by-invoice/pdf/${id}/by-id`;
    }
    return `${environment.BACKEND}/doc/shipment-by-invoice/pdf/${id}/by-article`;
  }

  getPdf(id: number, type: ShipmentByInvoicePrintType = 'article') {
    return this.http.get(this.getPdfUrl(id, type), { responseType: 'blob' });
  }

  productsToPayload(products: ShipmentByInvoiceItem[]): ShipmentByInvoiceProductPayload[] {
    return products.map((item) => ({
      product_id: Number(item.product.id),
      quan: Number(item.quan)
    }));
  }

  private normalizeListRow(row: ShipmentByInvoiceListRow): ShipmentByInvoiceListRow {
    return {
      ...row,
      date: new Date(row.date),
      date_created: new Date(row.date_created),
      sum: Number(row.sum)
    };
  }

  private normalizeDoc(doc: ShipmentByInvoiceDocument): ShipmentByInvoiceDocument {
    return {
      ...doc,
      date: new Date(doc.date),
      date_created: new Date(doc.date_created),
      sum: Number(doc.sum),
      products: (doc.products || []).map((item) => ({
        ...item,
        quan: Number(item.quan),
        price: Number(item.price)
      }))
    };
  }
}
